import { useParams, useNavigate } from "react-router-dom"
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Chip,
    Divider,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Avatar,
    Skeleton,
} from "@heroui/react"
import {
    ChevronLeft,
    Download,
    Eye,
    FileText,
    Calendar,
    CheckCircle,
    Clock,
    AlertCircle,
    BookOpen,
    GraduationCap,
    Hash,
    User,
    Trash2,
    Edit,
    Building2,
    ScrollText,
    Sparkles
} from "lucide-react"
import { useState, useMemo } from "react"
import { useGetPascoById, useApprovePasco, useDeletePasco, useReviewPasco, useScheduleReviewPasco, useDownloadPascoStream, useGetPascoDownloadUrl, useExtractPascoMetadata } from "@src/presentation/hooks/pasco"
import { useGetUniversityById } from "@src/presentation/hooks/universities"
import { ToastService } from "@src/shared/services/toast.service"
import { formatDateFull, formatDateHuman } from "@src/shared/utils/date-formatter"

export default function PascoDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [actionType, setActionType] = useState<"approve" | "delete" | "review" | "schedule-review" | null>(null)
    const [confirmModalOpen, setConfirmModalOpen] = useState(false)

    // Fetch PASCO details
    const { data: pasco, isLoading, error, refetch } = useGetPascoById(id || "")

    // Fetch university details
    const { data: university } = useGetUniversityById(pasco?.universityId)

    // Fetch download URL for PDF preview (auto-fetch once PASCO details are loaded)
    const {
        data: presignedUrl,
        isLoading: isLoadingUrl,
    } = useGetPascoDownloadUrl(id, {
        enabled: !!id && !!pasco, // Fetch when we have PASCO ID and PASCO details are loaded
    })

    // Mutations
    const { mutate: approvePasco, isPending: isApproving } = useApprovePasco({
        onSuccess: () => {
            ToastService.success("PASCO approved successfully")
            setConfirmModalOpen(false)
            refetch()
        },
        onError: (error) => {
            ToastService.error(`Failed to approve PASCO: ${error.message}`)
        }
    })

    const { mutate: downloadPascoStream, isPending: isDownloading } = useDownloadPascoStream({
        onSuccess: () => {
            ToastService.success("Download started successfully")
        },
        onError: (error) => {
            ToastService.error(`Failed to download PASCO: ${error.message}`)
        }
    })

    const { mutate: reviewPasco, isPending: isReviewing } = useReviewPasco({
        onSuccess: () => {
            ToastService.success("PASCO marked as reviewed")
            setConfirmModalOpen(false)
            refetch()
        },
        onError: (error) => {
            ToastService.error(`Failed to review PASCO: ${error.message}`)
        }
    })

    const { mutate: scheduleReviewPasco, isPending: isSchedulingReview } = useScheduleReviewPasco({
        onSuccess: () => {
            ToastService.success("PASCO scheduled for review")
            setConfirmModalOpen(false)
            refetch()
        },
        onError: (error) => {
            ToastService.error(`Failed to schedule review: ${error.message}`)
        }
    })

    const { mutate: deletePasco, isPending: isDeleting } = useDeletePasco({
        onSuccess: () => {
            ToastService.success("PASCO deleted successfully")
            navigate("/pascos")
        },
        onError: (error) => {
            ToastService.error(`Failed to delete PASCO: ${error.message}`)
        },
        retry: false,
    })

    // Extract metadata mutation
    const { mutate: extractMetadata, isPending: isExtracting } = useExtractPascoMetadata({
        onSuccess: (metadata) => {
            ToastService.success(metadata.message || "Metadata extracted successfully")
            // Refetch to get updated PASCO data
            refetch()
        },
        onError: (error) => {
            ToastService.error(`Failed to extract metadata: ${error.message}`)
        }
    })

    // Check if metadata is missing
    const isMissingMetadata = useMemo(() => {
        if (!pasco) return false
        return !pasco.courseCode || !pasco.courseName || !pasco.academicYear || !pasco.semester
    }, [pasco])

    const handleExtractMetadata = () => {
        if (!id) return
        extractMetadata(id)
    }

    const handleBackNavigation = () => {
        // Go back to the previous page in browser history
        navigate(-1)
    }

    // If PASCO not found or loading, show appropriate state
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50">
                {/* Header skeleton */}
                <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20 shadow-sm">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col sm:flex-row h-auto sm:h-16 py-3 sm:py-0 items-start sm:items-center justify-between gap-3 sm:gap-0">
                            <div className="flex items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                                <Skeleton className="h-8 w-20 rounded" />
                                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                    <Skeleton className="h-8 w-8 rounded-lg flex-shrink-0" />
                                    <div className="min-w-0 flex-1 space-y-1">
                                        <Skeleton className="h-5 w-48" />
                                        <Skeleton className="h-3 w-24" />
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                <Skeleton className="h-8 w-16 rounded" />
                                <Skeleton className="h-8 w-20 rounded" />
                            </div>
                        </div>
                    </div>
                </header>

                {/* Main content skeleton */}
                <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Left column - PASCO details */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* PDF Preview */}
                            <Card className="shadow-none border border-border rounded">
                                <CardHeader>
                                    <Skeleton className="h-6 w-32" />
                                </CardHeader>
                                <CardBody>
                                    <Skeleton className="h-96 w-full rounded" />
                                </CardBody>
                            </Card>
                        </div>

                        {/* Right column - Actions and metadata */}
                        <div className="space-y-6">
                            <Card className="shadow-none border border-border rounded">
                                <CardHeader>
                                    <Skeleton className="h-6 w-28" />
                                </CardHeader>
                                <CardBody>
                                    <div className="space-y-3">
                                        <Skeleton className="h-10 w-full rounded" />
                                        <Skeleton className="h-10 w-full rounded" />
                                        <Skeleton className="h-10 w-full rounded" />
                                    </div>
                                </CardBody>
                            </Card>

                            <Card className="shadow-none border border-border rounded">
                                <CardHeader>
                                    <Skeleton className="h-6 w-24" />
                                </CardHeader>
                                <CardBody>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <Skeleton className="h-8 w-8 rounded-full" />
                                            <div className="space-y-1">
                                                <Skeleton className="h-4 w-24" />
                                                <Skeleton className="h-3 w-16" />
                                            </div>
                                        </div>
                                        <Skeleton className="h-4 w-full" />
                                        <Skeleton className="h-4 w-3/4" />
                                    </div>
                                </CardBody>
                            </Card>
                        </div>
                    </div>
                </main>
            </div>
        )
    }

    if (error || !pasco) {
        // Don't show error state if we're currently deleting the pasco
        if (isDeleting) {
            return (
                <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50">
                    {/* Header skeleton */}
                    <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20 shadow-sm">
                        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="flex flex-col sm:flex-row h-auto sm:h-16 py-3 sm:py-0 items-start sm:items-center justify-between gap-3 sm:gap-0">
                                <div className="flex items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                                    <Skeleton className="h-8 w-20 rounded" />
                                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                        <Skeleton className="h-8 w-8 rounded-lg flex-shrink-0" />
                                        <div className="min-w-0 flex-1 space-y-1">
                                            <Skeleton className="h-5 w-48" />
                                            <Skeleton className="h-3 w-24" />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 w-full sm:w-auto">
                                    <Skeleton className="h-8 w-16 rounded opacity-50" />
                                    <Skeleton className="h-8 w-20 rounded opacity-50" />
                                </div>
                            </div>
                        </div>
                    </header>

                    {/* Delete overlay */}
                    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
                        <Card className="max-w-md shadow-lg border border-border rounded">
                            <CardBody className="text-center p-8">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                                <p className="text-default-600">Deleting PASCO...</p>
                            </CardBody>
                        </Card>
                    </div>

                    {/* Main content skeleton (dimmed) */}
                    <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 opacity-50">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* Left column - PASCO details */}
                            <div className="lg:col-span-2 space-y-6">
                                {/* Status and info cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Card className="shadow-none border border-border rounded">
                                        <CardHeader className="pb-3">
                                            <Skeleton className="h-5 w-24" />
                                        </CardHeader>
                                        <CardBody className="pt-0">
                                            <div className="space-y-3">
                                                <Skeleton className="h-4 w-full" />
                                                <Skeleton className="h-4 w-3/4" />
                                                <Skeleton className="h-4 w-1/2" />
                                            </div>
                                        </CardBody>
                                    </Card>

                                    <Card className="shadow-none border border-border rounded">
                                        <CardHeader className="pb-3">
                                            <Skeleton className="h-5 w-32" />
                                        </CardHeader>
                                        <CardBody className="pt-0">
                                            <div className="space-y-3">
                                                <Skeleton className="h-4 w-full" />
                                                <Skeleton className="h-4 w-2/3" />
                                                <Skeleton className="h-4 w-1/3" />
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>

                                {/* Course information */}
                                <Card className="shadow-none border border-border rounded">
                                    <CardHeader>
                                        <Skeleton className="h-6 w-40" />
                                    </CardHeader>
                                    <CardBody>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-4">
                                                <div>
                                                    <Skeleton className="h-4 w-20 mb-2" />
                                                    <Skeleton className="h-5 w-32" />
                                                </div>
                                                <div>
                                                    <Skeleton className="h-4 w-24 mb-2" />
                                                    <Skeleton className="h-5 w-28" />
                                                </div>
                                                <div>
                                                    <Skeleton className="h-4 w-16 mb-2" />
                                                    <Skeleton className="h-5 w-24" />
                                                </div>
                                            </div>
                                            <div className="space-y-4">
                                                <div>
                                                    <Skeleton className="h-4 w-18 mb-2" />
                                                    <Skeleton className="h-5 w-36" />
                                                </div>
                                                <div>
                                                    <Skeleton className="h-4 w-22 mb-2" />
                                                    <Skeleton className="h-5 w-30" />
                                                </div>
                                                <div>
                                                    <Skeleton className="h-4 w-20 mb-2" />
                                                    <Skeleton className="h-5 w-26" />
                                                </div>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>

                                {/* PDF Preview */}
                                <Card className="shadow-none border border-border rounded">
                                    <CardHeader>
                                        <Skeleton className="h-6 w-32" />
                                    </CardHeader>
                                    <CardBody>
                                        <Skeleton className="h-96 w-full rounded" />
                                    </CardBody>
                                </Card>
                            </div>

                            {/* Right column - Actions and metadata */}
                            <div className="space-y-6">
                                <Card className="shadow-none border border-border rounded">
                                    <CardHeader>
                                        <Skeleton className="h-6 w-28" />
                                    </CardHeader>
                                    <CardBody>
                                        <div className="space-y-3">
                                            <Skeleton className="h-10 w-full rounded opacity-50" />
                                            <Skeleton className="h-10 w-full rounded opacity-50" />
                                            <Skeleton className="h-10 w-full rounded opacity-50" />
                                        </div>
                                    </CardBody>
                                </Card>

                                <Card className="shadow-none border border-border rounded">
                                    <CardHeader>
                                        <Skeleton className="h-6 w-24" />
                                    </CardHeader>
                                    <CardBody>
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-3">
                                                <Skeleton className="h-8 w-8 rounded-full" />
                                                <div className="space-y-1">
                                                    <Skeleton className="h-4 w-24" />
                                                    <Skeleton className="h-3 w-16" />
                                                </div>
                                            </div>
                                            <Skeleton className="h-4 w-full" />
                                            <Skeleton className="h-4 w-3/4" />
                                        </div>
                                    </CardBody>
                                </Card>
                            </div>
                        </div>
                    </main>
                </div>
            )
        }

        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Card className="max-w-md shadow-none border border-border rounded">
                    <CardBody className="text-center p-8">
                        <AlertCircle className="h-12 w-12 text-danger mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-foreground mb-2">PASCO Not Found</h2>
                        <p className="text-default-500 mb-4">
                            {error?.message || "The PASCO you're looking for doesn't exist or has been removed."}
                        </p>
                        <Button color="primary" onPress={handleBackNavigation}>
                            Back to PASCOs
                        </Button>
                    </CardBody>
                </Card>
            </div>
        )
    }

    const handleDownload = async () => {
        if (!id || !pasco) return;

        // Generate filename based on PASCO details
        const filename = `${pasco.courseCode}_${pasco.semester}_${pasco.academicYear}.pdf`.replace(/\s+/g, '_');

        // Use the new stream endpoint
        downloadPascoStream({ pascoId: id, filename });
    }

    const handleAction = (type: "approve" | "delete" | "review" | "schedule-review") => {
        setActionType(type)
        setConfirmModalOpen(true)
    }

    const handleEditClick = () => {
        navigate(`/pascos/${id}/edit`)
    }

    const confirmAction = () => {
        if (!actionType || !id) return

        if (actionType === "approve") {
            approvePasco(id)
        } else if (actionType === "review") {
            reviewPasco(id)
        } else if (actionType === "schedule-review") {
            scheduleReviewPasco(id)
        } else if (actionType === "delete") {
            deletePasco(id)
        }
    }

    // Helper function to get status color
    const getStatusColor = (status: string) => {
        switch (status) {
            case "completed":
                return "success"
            case "processing":
                return "primary"
            case "failed":
                return "danger"
            case "pending":
                return "warning"
            default:
                return "default"
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case "completed":
                return <CheckCircle className="h-4 w-4" />
            case "processing":
                return <Clock className="h-4 w-4" />
            case "failed":
                return <AlertCircle className="h-4 w-4" />
            case "pending":
                return <Clock className="h-4 w-4" />
            default:
                return null
        }
    }

    return (
        <div className="h-full overflow-y-auto lg:overflow-hidden bg-gradient-to-br from-background via-background to-default-50 flex flex-col">
            {/* Header with gradient */}
            <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 z-20 shadow-sm flex-shrink-0">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row h-auto sm:h-16 py-3 sm:py-0 items-start sm:items-center justify-between gap-3 sm:gap-0">
                        <div className="flex items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                            <Button
                                variant="light"
                                size="sm"
                                onPress={handleBackNavigation}
                                startContent={<ChevronLeft className="h-4 w-4" />}
                                className="hover:scale-105 transition-transform"
                            >
                                Back
                            </Button>
                            <Divider orientation="vertical" className="h-6 hidden sm:block" />
                            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-600 shadow-md flex-shrink-0">
                                    <FileText className="h-4 sm:h-5 w-4 sm:w-5 text-primary-foreground" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h1 className="text-sm sm:text-xl font-semibold text-foreground truncate max-w-[200px] sm:max-w-[300px] lg:max-w-[400px]\" title={`${pasco.courseCode || ''} - ${pasco.courseName || ''}`}>
                                        {pasco.courseCode || 'N/A'} - {pasco.courseName || 'Untitled'}
                                    </h1>
                                    <p className="text-xs text-default-500">{pasco.academicYear || 'No year'}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto flex-wrap justify-end">
                            {!isMissingMetadata && (
                                <Button
                                    variant="flat"
                                    color="default"
                                    onPress={handleExtractMetadata}
                                    startContent={<Sparkles className="h-3 sm:h-4 w-3 sm:w-4" />}
                                    className="rounded"
                                    size="sm"
                                    isLoading={isExtracting}
                                    isIconOnly={true}
                                    title="Re-extract metadata with AI"
                                />
                            )}
                            <Button
                                variant="flat"
                                color="default"
                                onPress={handleEditClick}
                                startContent={<Edit className="h-3 sm:h-4 w-3 sm:w-4" />}
                                className="rounded"
                                size="sm"
                                isIconOnly={true}
                            />
                            <Button
                                variant="flat"
                                color="danger"
                                onPress={() => handleAction("delete")}
                                startContent={<Trash2 className="h-3 sm:h-4 w-3 sm:w-4" />}
                                className="rounded"
                                size="sm"
                                isIconOnly={true}
                            />
                            <Button
                                variant="flat"
                                color="default"
                                onPress={handleDownload}
                                startContent={<Download className="h-3 sm:h-4 w-3 sm:w-4" />}
                                className="rounded"
                                isLoading={isDownloading}
                                size="sm"
                                isIconOnly={true}
                            />
                            {!pasco.isApproved && pasco.processingStatus === "pending" && (
                                <Button
                                    color="success"
                                    onPress={() => handleAction("approve")}
                                    startContent={<CheckCircle className="h-3 sm:h-4 w-3 sm:w-4" />}
                                    className="rounded shadow-sm"
                                    size="sm"
                                >
                                    <span className="hidden sm:inline">Approve</span>
                                </Button>
                            )}
                            {!pasco.isReviewed ?
                                <Button
                                    color="success"
                                    onPress={() => handleAction("review")}
                                    startContent={<CheckCircle className="h-3 sm:h-4 w-3 sm:w-4" />}
                                    className="rounded shadow-sm"
                                    size="sm"
                                >
                                    <span className="hidden sm:inline">Mark Reviewed</span>
                                    <span className="sm:hidden">Review</span>
                                </Button>
                                : <Button
                                    color="warning"
                                    variant="flat"
                                    onPress={() => handleAction("schedule-review")}
                                    startContent={<Clock className="h-3 sm:h-4 w-3 sm:w-4" />}
                                    className="rounded"
                                    size="sm"
                                >
                                    <span className="hidden sm:inline">Schedule Review</span>
                                    <span className="sm:hidden">Schedule</span>
                                </Button>
                            }
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex-1 lg:min-h-0 lg:overflow-hidden flex flex-col">
                {/* Missing Metadata Banner */}
                {isMissingMetadata && (
                    <Card className="rounded border border-warning/50 bg-warning/5 shadow-none mb-4 flex-shrink-0">
                        <CardBody className="p-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-warning/10 flex-shrink-0">
                                        <Sparkles className="h-5 w-5 text-warning" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-warning-700 dark:text-warning">Missing Metadata</p>
                                        <p className="text-sm text-foreground-600 mt-0.5">
                                            This PASCO is missing course information. Use AI to automatically extract metadata from the document.
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    color="warning"
                                    variant="flat"
                                    startContent={<Sparkles className="h-4 w-4" />}
                                    onPress={handleExtractMetadata}
                                    isLoading={isExtracting}
                                    className="rounded flex-shrink-0"
                                    size="sm"
                                >
                                    {isExtracting ? "Extracting..." : "Extract with AI"}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 lg:min-h-0 lg:overflow-hidden">
                    {/* Left Column - PDF Viewer */}
                    <div className="lg:col-span-2 overflow-hidden min-h-[60vh] lg:min-h-0">
                        <Card className="shadow-none border border-border rounded h-full flex flex-col">
                            <CardHeader className="flex flex-row items-center justify-between border-b border-divider pb-4 flex-shrink-0">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-lg bg-primary/10">
                                        <Eye className="h-5 w-5 text-primary" />
                                    </div>
                                    <h2 className="text-lg font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                        PDF Preview
                                    </h2>
                                </div>
                            </CardHeader>
                            <CardBody className="p-0 flex-1 overflow-hidden min-h-0">
                                <div className="bg-default-50 rounded-b-xl overflow-hidden h-full">
                                    {isLoadingUrl ? (
                                        <div className="w-full flex h-full p-4">
                                            <Skeleton className="w-full h-full rounded" />
                                        </div>
                                    ) : presignedUrl ? (
                                        <embed
                                            src={`${presignedUrl.downloadUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                                            type="application/pdf"
                                            className="w-full flex h-full border-0"
                                            title={`${pasco.courseCode} PDF`}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <div className="text-center">
                                                <AlertCircle className="h-12 w-12 text-warning mx-auto mb-4" />
                                                <p className="text-default-600">Unable to load PDF preview. Please use the Download button above.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    </div>

                    {/* Right Column - Metadata */}
                    <div className="space-y-6 lg:overflow-y-auto lg:min-h-0 scrollbar-hide pb-6">
                        {/* Status Card */}
                        <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
                            <CardHeader className="border-b border-divider pb-3">
                                <h3 className="text-sm font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                    Status
                                </h3>
                            </CardHeader>
                            <CardBody className="p-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-3 rounded-lg bg-default-50 hover:bg-default-100 transition-colors">
                                        <p className="text-xs text-default-500 mb-2">Processing</p>
                                        <Chip
                                            color={getStatusColor(pasco.processingStatus)}
                                            variant="flat"
                                            size="sm"
                                            startContent={getStatusIcon(pasco.processingStatus)}
                                        >
                                            {pasco.processingStatus.charAt(0).toUpperCase() + pasco.processingStatus.slice(1)}
                                        </Chip>
                                    </div>
                                    <div className="p-3 rounded-lg bg-default-50 hover:bg-default-100 transition-colors">
                                        <p className="text-xs text-default-500 mb-2">Approval</p>
                                        <Chip
                                            color={pasco.isApproved ? "success" : "warning"}
                                            variant="flat"
                                            size="sm"
                                            startContent={pasco.isApproved ? <CheckCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                                        >
                                            {pasco.isApproved ? "Approved" : "Pending"}
                                        </Chip>
                                    </div>
                                    <div className="p-3 rounded-lg bg-default-50 hover:bg-default-100 transition-colors">
                                        <p className="text-xs text-default-500 mb-2">Review</p>
                                        <Chip
                                            color={pasco.isReviewed ? "success" : "warning"}
                                            variant="flat"
                                            size="sm"
                                            startContent={pasco.isReviewed ? <CheckCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                                        >
                                            {pasco.isReviewed ? "Reviewed" : "Pending"}
                                        </Chip>
                                    </div>
                                    <div className="p-3 rounded-lg bg-default-50 hover:bg-default-100 transition-colors">
                                        <p className="text-xs text-default-500 mb-2">Type</p>
                                        <Chip
                                            color={pasco.isSupplementary ? "secondary" : "default"}
                                            variant="flat"
                                            size="sm"
                                        >
                                            {pasco.isSupplementary ? "Supplementary" : "Regular"}
                                        </Chip>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Course Information */}
                        <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
                            <CardHeader className="border-b border-divider pb-3">
                                <h3 className="text-sm font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                    Course Information
                                </h3>
                            </CardHeader>
                            <CardBody className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-primary/10">
                                        <BookOpen className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Course Code</p>
                                        <p className="text-sm font-semibold text-foreground">{pasco.courseCode}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-secondary/10">
                                        <FileText className="h-4 w-4 text-secondary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Course Name</p>
                                        <p className="text-sm font-semibold text-foreground">{pasco.courseName}</p>
                                    </div>
                                </div>
                                {pasco.department && (
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 rounded-lg bg-warning/10">
                                            <Building2 className="h-4 w-4 text-warning" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Department</p>
                                            <p className="text-sm font-semibold text-foreground">{pasco.department}</p>
                                        </div>
                                    </div>
                                )}
                                <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-success/10">
                                        <GraduationCap className="h-4 w-4 text-success" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Level</p>
                                        <p className="text-sm font-semibold text-foreground">Level {pasco.level}</p>
                                    </div>
                                </div>
                                {pasco.courseDescription && (
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 rounded-lg bg-primary/10">
                                            <ScrollText className="h-4 w-4 text-primary" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Course Description</p>
                                            <p className="text-sm text-foreground leading-relaxed mt-1">{pasco.courseDescription}</p>
                                        </div>
                                    </div>
                                )}
                            </CardBody>
                        </Card>



                        {/* Academic Details */}
                        <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
                            <CardHeader className="border-b border-divider pb-3">
                                <h3 className="text-sm font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">Academic Details</h3>
                            </CardHeader>
                            <CardBody className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-primary/10">
                                        <Calendar className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Academic Year</p>
                                        <p className="text-sm font-semibold text-foreground">{pasco.academicYear}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-secondary/10">
                                        <Hash className="h-4 w-4 text-secondary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Semester</p>
                                        <p className="text-sm font-semibold text-foreground">
                                            {pasco.semester === 1 ? "First Semester" : "Second Semester"}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-success/10">
                                        <Calendar className="h-4 w-4 text-success" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Created Date</p>
                                        <p className="text-sm font-semibold text-foreground">
                                            {formatDateFull(pasco.createdAt)}
                                        </p>
                                        <p className="text-xs text-default-400 mt-0.5">
                                            {formatDateHuman(pasco.createdAt)}
                                        </p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        {/* University Information */}
                        <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
                            <CardHeader className="border-b border-divider pb-3">
                                <h3 className="text-sm font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                    University Information
                                </h3>
                            </CardHeader>
                            <CardBody className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-primary/10">
                                        <Building2 className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium">University</p>
                                        <p className="text-sm font-semibold text-foreground">{university?.name || 'Unknown University'}</p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Uploaded By User */}
                        <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
                            <CardHeader className="border-b border-divider pb-3">
                                <h3 className="text-sm font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                    Uploaded By
                                </h3>
                            </CardHeader>
                            <CardBody className="space-y-3">
                                <div className="flex items-start gap-3">
                                    {pasco.uploadedBy ? (
                                        <>
                                            {pasco.uploadedBy.profilePicture ? (
                                                <Avatar
                                                    src={pasco.uploadedBy.profilePicture}
                                                    name={pasco.uploadedBy.name}
                                                    size="md"
                                                    className="flex-shrink-0"
                                                />
                                            ) : (
                                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-600 shadow-md flex-shrink-0">
                                                    <User className="h-5 w-5 text-primary-foreground" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-foreground truncate">
                                                    {pasco.uploadedBy.name}
                                                </p>
                                                <p className="text-xs text-default-500 mt-0.5">
                                                    Uploaded {formatDateHuman(pasco.createdAt)}
                                                </p>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="flex items-center gap-2 text-default-500">
                                            <User className="h-5 w-5" />
                                            <span className="text-sm">Unknown uploader</span>
                                        </div>
                                    )}
                                </div>
                            </CardBody>
                        </Card>

                        {/* Additional Information */}
                        <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
                            <CardHeader className="border-b border-divider pb-3">
                                <h3 className="text-sm font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">Additional Information</h3>
                            </CardHeader>
                            <CardBody>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center p-2 rounded-lg bg-default-50 hover:bg-default-100 transition-colors">
                                        <span className="text-xs text-default-500 uppercase tracking-wide font-medium">PASCO ID</span>
                                        <span className="text-xs font-mono font-semibold text-foreground">{pasco.pascoId.substring(0, 8)}...</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 rounded-lg bg-default-50 hover:bg-default-100 transition-colors">
                                        <span className="text-xs text-default-500 uppercase tracking-wide font-medium">File Format</span>
                                        <span className="text-sm font-semibold text-foreground">PDF</span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </main>

            {/* Confirmation Modal */}
            <Modal
                isOpen={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                classNames={{
                    base: "rounded border border-border",
                }}
                backdrop="blur"
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            {actionType === "delete" ? (
                                <Trash2 className="h-5 w-5 text-danger" />
                            ) : actionType === "approve" ? (
                                <CheckCircle className="h-5 w-5 text-success" />
                            ) : actionType === "review" ? (
                                <CheckCircle className="h-5 w-5 text-success" />
                            ) : (
                                <Clock className="h-5 w-5 text-warning" />
                            )}
                            <span>
                                {actionType === "delete" ? "Delete PASCO" :
                                    actionType === "approve" ? "Approve PASCO" :
                                        actionType === "review" ? "Mark PASCO as Reviewed" :
                                            "Schedule PASCO for Review"}
                            </span>
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        <p className="text-sm text-default-600">
                            {actionType === "delete" ? "Are you sure you want to delete this PASCO? This action cannot be undone." :
                                actionType === "approve" ? "Are you sure you want to approve this PASCO? It will be processed and made available for analytics." :
                                    actionType === "review" ? "Are you sure you want to mark this PASCO as reviewed? This indicates that you have verified the PASCO information matches the metadata and content." :
                                        "Are you sure you want to schedule this PASCO for review? This will mark it as needing review again."}
                        </p>
                        <div className="mt-4 p-3 bg-default-100 rounded border border-border">
                            <div className="space-y-2 text-sm">
                                <div>
                                    <span className="text-default-500">Course: </span>
                                    <span className="font-medium">{pasco?.courseCode || 'N/A'} - {pasco?.courseName || 'Untitled'}</span>
                                </div>
                                {pasco?.academicYear && (
                                    <div>
                                        <span className="text-default-500">Academic Year: </span>
                                        <span className="font-medium">{pasco.academicYear}</span>
                                    </div>
                                )}
                                <div>
                                    <span className="text-default-500">Created: </span>
                                    <span className="font-medium">{pasco?.createdAt && formatDateHuman(pasco.createdAt)}</span>
                                </div>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="flat"
                            onPress={() => setConfirmModalOpen(false)}
                            className="rounded"
                        >
                            Cancel
                        </Button>
                        <Button
                            color={
                                actionType === "approve" || actionType === "review" ? "success" :
                                    actionType === "schedule-review" ? "warning" : "danger"
                            }
                            onPress={confirmAction}
                            isLoading={isApproving || isDeleting || isReviewing || isSchedulingReview}
                            className="rounded"
                        >
                            {actionType === "delete" ? "Delete" :
                                actionType === "approve" ? "Approve" :
                                    actionType === "review" ? "Mark as Reviewed" :
                                        actionType === "schedule-review" ? "Schedule for Review" : "Reject"}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    )
}
